// =====================================================================
// White-label branding.
//
// Kaynak: backend (POST /User/Init/InitApp) domaine göre `branding` objesini
// döndürür (tourzone.com -> Tourzone, holideus -> Holideus). Frontend bunu
// state.Apps.branding'e koyar ve applyBrand() ile CSS değişkenlerine yazar.
//
// DEFAULT_BRANDING: API yanıtı gelmeden önceki ilk boyama + fallback (Tourzone).
// =====================================================================

export const DEFAULT_BRANDING = {
    brand: "Tourzone",
    // "Powered by <platform>" adı (backend Brand::platformName) + satırı kapatma bayrağı.
    // Bkz. Libs/Branding/platform.js.
    platformName: "TourZone",
    poweredBy: true,
    logo: "/Assets/img/tz_white.svg",
    icon: "/Assets/images/tourzoneIcons.svg",
    logoSignup: "/Assets/images/logo_white.svg",
    signupBg: "/Assets/images/signup-bg.jpg",
    loginBg: "/Assets/images/login-bg.jpeg",
    favicon: "/favicon.ico",
    primaryColor: "#41009a",
    primaryColorLight: "#a9bbff",
    siteName: "Partners | Tourzone V1.1",
    description: "Dünyanın dört bir yanından turları, turistik yerleri, gezileri, yapılacak şeyleri ve eğlenceli etkinlikleri bulun, karşılaştırın ve rezervasyon yapın.",
    supportPhone: "",
    companyInfoBg: "#273643",
    companyInfoTypeBg: "#072b4e",
    payment: "stripe",
    sharelink: "https://sharelink.tourzone.com",
    // Dağıtım markasının varsayılanları. API'den gelen branding bunları ezer
    // (Holideus -> ru / RUB). Kullanıcının AÇIK dil/para seçimi her ikisini de ezer.
    defaultLang: "tr",
    defaultCurrency: "EUR",
    // Ant Design ConfigProvider token'ları. DEĞERLER LAYOUT'TA BUGÜN KULLANILANLARLA
    // BİREBİR AYNI olmalı — Layouts.js bunları buradan okuyor, sapma olursa TourZone'un
    // görünümü sessizce değişir.
    colorPrimary: "#41009a",
    colorLink: "#2d0066",
    components: {
        Layout: {
            siderBg: "#1e1033",
            triggerBg: "#160a28",
            triggerColor: "#41009a",
            headerBg: "#ffffff",
            footerBg: "#f7f5f0",
        },
        Menu: {
            darkItemBg: "transparent",
            darkSubMenuItemBg: "transparent",
            darkItemSelectedBg: "rgba(65, 0, 154, 0.18)",
            darkItemSelectedColor: "#c4b5fd",
            darkItemHoverBg: "rgba(65, 0, 154, 0.08)",
            darkItemHoverColor: "#ffffff",
            darkItemColor: "rgba(255,255,255,0.78)",
        },
        Button: {
            colorPrimaryHover: "#2d0066",
        },
    },
};

const hexToRgb = (hex) => {
    const h = String(hex || '').replace('#', '');
    if (!/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(h)) return '65, 0, 154';
    const f = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
    const n = parseInt(f, 16);
    return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
};

// API'den gelen branding (varsa) DEFAULT üzerine birleştirilir.
export const mergeBranding = (apiBranding) => ({
    ...DEFAULT_BRANDING,
    ...(apiBranding || {}),
    components: {
        ...DEFAULT_BRANDING.components,
        ...((apiBranding && apiBranding.components) || {}),
    },
});

// Login/index gibi senkron yerler için fallback (API gelmeden).
export const getBrand = () => DEFAULT_BRANDING;

export const applyBrand = (input) => {
    if (typeof document === 'undefined') return;
    const b = mergeBranding(input);
    const root = document.documentElement;
    const primary = b.primaryColor || '#41009a';
    const primaryLight = b.primaryColorLight || '#a9bbff';

    // Marka rengi — _auth.scss --brand-gold* değişkenlerini okur (geriye uyumlu isim).
    root.style.setProperty('--brand-gold', primary);
    root.style.setProperty('--brand-gold-light', primaryLight);
    root.style.setProperty('--brand-gold-dark', primary);
    root.style.setProperty('--brand-gold-rgb', hexToRgb(primary));
    root.style.setProperty('--brand-primary', primary);
    root.style.setProperty('--brand-primary-light', primaryLight);

    // Hero arka planı: marka login görseli ÜSTTE, altında marka rengi gradyanı.
    // Görsel 404 olsa bile (dosya eklenmemişse) gradyan görünür -> hero asla boş kalmaz.
    const colorFallback = `linear-gradient(135deg, ${primary} 0%, #0f1620 70%)`;
    root.style.setProperty(
        '--brand-login-bg',
        b.loginBg ? `center / cover no-repeat url("${b.loginBg}"), ${colorFallback}` : colorFallback
    );
    root.style.setProperty(
        '--brand-signup-bg',
        b.signupBg ? `center / cover no-repeat url("${b.signupBg}"), ${colorFallback}` : colorFallback
    );

    // Layout/Menu tema token'ları (ileride ConfigProvider/Layout için CSS var olarak hazır).
    const L = b.components?.Layout || {};
    const M = b.components?.Menu || {};
    root.style.setProperty('--brand-sider-bg', L.siderBg || '#001529');
    root.style.setProperty('--brand-header-bg', L.headerBg || '#001529');
    root.style.setProperty('--brand-body-bg', L.bodyBg || '#f5f5f5');
    root.style.setProperty('--brand-menu-selected-bg', M.darkItemSelectedBg || '#1677ff');
    root.style.setProperty('--brand-company-info-bg', b.companyInfoBg || '#273643');

    root.setAttribute('data-brand', (b.brand || 'tourzone').toLowerCase());
    if (b.siteName) document.title = b.siteName;
    if (b.favicon) {
        let link = document.querySelector("link[rel~='icon']");
        if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
        }
        link.href = b.favicon;
    }
};
